import { Button, Chip } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../hooks/useConfirm';
import { useElapsedMs } from '../../../hooks/useElapsedMs';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { SolverEngine } from '../../../types/planung-graphql-client-defs';
import { advanceOpenTopics, type OpenTopicsState } from '../../../utils/timetable/openTopics';
import {
  formatClock,
  GAP_INITIALIZING_THRESHOLD,
  gapFormatter,
  isConflictFreeSolution,
  parseScoreDetails,
} from '../../../utils/timetable/solverDiagnostics';
import { solverCountdown } from '../../../utils/timetable/solverDuration';
import { ScoreDebugModal } from '../ScoreDebugModal/ScoreDebugModal';
import { useTimetableStore } from '../TimetableProvider';
import { useConstraintLabel } from '../useConstraintLabel';
import styles from './SolverRunBanner.module.scss';

/**
 * Inline solver status that replaces the card picker inside GridActionArea while a
 * solver job runs. It is the single status surface (the solver wizard no longer hosts
 * a running step), mirroring how CardControl / CardTimefoldAnalyze take over the area.
 */
export const SolverRunBanner: FC = observer(() => {
  const { t } = useTranslation();
  const constraintLabel = useConstraintLabel();
  const pinboardStore = useTimetableStore();
  const solver = pinboardStore.solverStore;
  const perms = usePermissionChecker();
  const isAdmin = perms?.isOmniAdmin() ?? false;
  const [scoreDebugOpen, setScoreDebugOpen] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const { confirm, ConfirmationDialog } = useConfirm();

  const job = solver.solverJob;
  const elapsedMs = useElapsedMs(job?.createdAt);
  // Wann der Solver zuletzt einen besseren Plan gemeldet hat (Monitor des Backends) – daraus ergibt sich,
  // wann ein „Guter Plan“ von selbst endet und übernommen wird.
  const parsedDetails = parseScoreDetails(job?.scoreDetails);
  const lastImprovedAt =
    parsedDetails && parsedDetails !== 'error' ? (parsedDetails.monitor?.scoreLastChangedAt ?? null) : null;
  const sinceImprovedMs = useElapsedMs(lastImprovedAt ?? undefined);

  // „Noch offen“: größte Strafthemen des aktuell besten Plans mit Trend zum vorigen Poll. Der Zustand
  // hängt am Poll (`monitor.lastPolledAt`), nicht am Render – das Banner rendert jede Sekunde neu.
  const details = parsedDetails && parsedDetails !== 'error' ? parsedDetails : null;
  const breakdownRows = details?.breakdownFinal?.top ?? details?.breakdownLight?.top ?? null;
  const pollKey = details?.monitor?.lastPolledAt ?? String(details?.monitor?.pollCount ?? job?.scoreDetails ?? '');
  const openTopicsRef = useRef<OpenTopicsState | null>(null);
  openTopicsRef.current = job
    ? advanceOpenTopics(openTopicsRef.current, { jobId: job.jobId, pollKey, rows: breakdownRows })
    : null;
  // Nur zeigen, was zu diesem Poll gehört – ohne Breakdown (z. B. konfliktfreie Phase) keine alte Liste.
  const openTopics = breakdownRows && openTopicsRef.current?.pollKey === pollKey ? openTopicsRef.current.topics : [];

  // Guard against the (rare) case where the parent renders us without a job.
  if (!job) {
    return null;
  }

  const elapsedText = elapsedMs == null ? '–' : formatClock(elapsedMs);

  // Wann der Lauf von selbst endet – das Backend übernimmt den Plan dann automatisch. Nur der Generator
  // (cp-sat) hat die Zeitregeln aus SOLVER_TIMING; „Bestmöglich“ endet nicht vorzeitig (kein Countdown).
  const countdown =
    job.engine === SolverEngine.Timefold
      ? solverCountdown(solver.solveOptions.speed, elapsedMs, sinceImprovedMs)
      : null;
  const countdownLine = (): string | null => {
    if (!countdown) {
      return null;
    }
    if (countdown.remainingMs <= 0) {
      return t('pinboard.generate.runBanner.applySoon');
    }
    // Aufrunden: bei 0,4 s Rest „0:01“ statt „0:00“.
    const time = formatClock(Math.ceil(countdown.remainingMs / 1000) * 1000);
    return countdown.kind === 'idle'
      ? t('pinboard.generate.runBanner.applyWhenIdle', { time })
      : t('pinboard.generate.runBanner.applyAtLatest', { time });
  };

  const mathplanGapPercent =
    isAdmin &&
    job.engine === SolverEngine.Mathplan &&
    Number.isFinite(job.gap) &&
    job.gap >= 0 &&
    job.gap < GAP_INITIALIZING_THRESHOLD
      ? gapFormatter.format(job.gap * 100)
      : null;

  const hasPreview = solver.previewPlacements.size > 0;

  // `solutionFound` only says an incumbent exists (Timefold/cp-sat: any scored plan, which may still
  // break hard rules or leave cards open), so the "optimize" phase additionally needs a conflict-free plan.
  const phase: 'search' | 'optimize' = isConflictFreeSolution({
    engine: job.engine,
    solutionFound: job.solutionFound,
    scoreDetails: job.scoreDetails,
    unplacedCount: solver.previewUnplacedCount,
  })
    ? 'optimize'
    : 'search';

  // Statuszeile unter dem Titel: offene Kärtchen, sonst was der Lauf gerade tut. „Noch 0 Kärtchen offen“
  // ließ fragen, warum der Lauf noch läuft. Sind alle gesetzt, aber Konflikte offen, keine eigene Zeile –
  // welche, steht darunter unter „Noch offen“.
  const statusLine = (): string | null => {
    if (solver.previewUnplacedCount > 0) {
      return t('pinboard.generate.runBanner.unplaced', { count: solver.previewUnplacedCount });
    }
    if (phase === 'search') {
      return null;
    }
    // „Bestmöglich“ (COMPLEX) läuft bis zum Ende seines Budgets; die anderen enden von selbst – wann,
    // zeigt der Countdown (dann keine zweite Zeile dazu).
    if (solver.solveOptions.speed === 'thorough') {
      return t('pinboard.generate.runBanner.improving');
    }
    return countdown ? null : t('pinboard.generate.runBanner.endsByItself');
  };

  // Roh-Ids nur für Admins (hinter dem Text), Nutzer sehen den verständlichen Namen.
  const openTopicLabel = (id: string): string => {
    const label = constraintLabel(id);
    return isAdmin && label === t('pinboard.generate.runBanner.openTopics.otherRule') ? `${label} (${id})` : label;
  };

  // Nur die Themen – ohne Zahl (was gezählt wird, ist je Regel verschieden und ohne Einheit nicht
  // verständlich; Admins sehen die Zahlen im Score-Debug) und ohne Trendpfeil (ein ↑ sah nach Rückschritt
  // aus, obwohl der Plan insgesamt besser wurde).
  const openTopicsLine = openTopics.length > 0 ? openTopics.map((topic) => openTopicLabel(topic.id)).join(' · ') : null;

  const options = solver.solveOptions;
  const optionChips = [
    options.keepExistingPositions && t('pinboard.generate.runBanner.optionChip.keepCards'),
    options.includeRoomResources && t('pinboard.generate.runBanner.optionChip.withRooms'),
    options.includeSubjectContainer && t('pinboard.generate.runBanner.optionChip.subjectContainer'),
  ].filter((label): label is string => Boolean(label));

  return (
    <div className={styles.banner} role='status' aria-live='polite'>
      <div className={styles.text}>
        {/* Kleiner Spinner vor dem Titel, Laufzeit dezent dahinter – grün, sobald der Plan konfliktfrei ist. */}
        <div className={styles.title}>
          <span
            className={classNames(styles.spinner, { [styles['conflict-free']]: phase === 'optimize' })}
            aria-hidden='true'
          />
          <span>
            {phase === 'optimize'
              ? t('pinboard.generate.runBanner.conflictFree')
              : t('pinboard.generate.runBanner.running')}
          </span>
          <span
            className={styles.elapsed}
            role='timer'
            aria-label={t('pinboard.generate.runBanner.meta', { elapsed: elapsedText })}
          >
            · {elapsedText}
          </span>
        </div>
        {/* Wann der Plan von selbst übernommen wird (Entwurf, Guter Plan). */}
        {countdownLine() ? <div className={styles.meta}>{countdownLine()}</div> : null}
        {hasPreview && statusLine() ? <div className={styles.unplaced}>{statusLine()}</div> : null}
        {openTopicsLine ? (
          <div className={styles['open-topics']}>
            {t('pinboard.generate.runBanner.openTopics.label')} {openTopicsLine}
          </div>
        ) : null}
        {optionChips.length > 0 ? (
          <div className={styles.chips}>
            {optionChips.map((label) => (
              <Chip key={label} value={label} bgColor='var(--color-primary-light)' color='var(--color-grey-darkest)' />
            ))}
          </div>
        ) : null}
        {mathplanGapPercent != null ? (
          <div className={styles.gap}>
            {t('pinboard.generate.wizard.step3.gapLine', { percent: mathplanGapPercent })}
          </div>
        ) : null}
      </div>
      {/* Unten rechts: [Score-Debug] [Generierung abbrechen] [Plan übernehmen]. */}
      <div className={styles.actions}>
        <div className={styles.buttons}>
          {isAdmin ? (
            <Button hierarchy='tertiary' onClick={() => setScoreDebugOpen(true)}>
              {t('pinboard.generate.runBanner.scoreDebug')}
            </Button>
          ) : null}
          <Button
            hierarchy='secondary'
            onClick={async () => {
              // Discarding throws away the running solver's result, so confirm first — this is
              // the escape hatch for a hung job, not a routine action.
              const confirmed = await confirm({
                title: t('pinboard.generate.runBanner.discardConfirm.title'),
                message: t('pinboard.generate.runBanner.discardConfirm.message'),
                confirmText: t('pinboard.generate.runBanner.discardConfirm.confirm'),
                cancelText: t('common.cancel'),
              });
              if (!confirmed) {
                return;
              }
              // abortJob() cancels server-side, clears the snapshot and unmounts this banner,
              // so there is nothing to reset on success.
              setDiscarding(true);
              void solver.abortJob().finally(() => setDiscarding(false));
            }}
            disabled={solver.accepting || discarding}
            isLoading={discarding}
          >
            {t('pinboard.generate.runBanner.discardSolve')}
          </Button>
          <Button
            hierarchy='primary'
            onClick={() => void solver.acceptCurrentSolution()}
            disabled={solver.accepting || discarding || !(job.solutionFound || hasPreview)}
            isLoading={solver.accepting}
          >
            {t('pinboard.generate.runBanner.acceptNow')}
          </Button>
        </div>
      </div>
      {isAdmin ? <ScoreDebugModal isOpen={scoreDebugOpen} onClose={() => setScoreDebugOpen(false)} /> : null}
      <ConfirmationDialog />
    </div>
  );
});
